import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Flex, message, Modal, Spin, Tooltip, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import React, { useState } from "react";
import { EntFinancialIndicatorsFilter } from "../../../models/filters/financialIndicatorsFilter";
import { useFinancialIndicators } from "./useFinancialIndicators";
import { ContentPartFilter } from "../../../components/content-part/ContentPartFilter";
import { AddFinancialIndicatorsModal } from "./AddFinancialIndicatorsModal";
import { PageHeader } from "../../../components/page-header";
import { FilterFinancialIndicators } from "./FilterFinancialIndicators";
import { EmptyData } from "../../../components/empty/emptyData";
import { EntFinancialIndicators } from "../../../models/mechanization/financialIndicators";
import { useGetDataOnScroll } from "../../../components/getDataOnScrollProps/useGetDataOnScrollProps";
import {
  useLazyGetFinancialIndicatorsListFilteredQuery,
  useReportFinancialIndicatorsMutation
} from "../../../redux/api/financialIndicatorsApi";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/root/root-state.interface";

enum FormField {
  governmentIds,
  periodStart,
  periodEnd
}

export const FinancialIndicators = () => {
  const [modal, contextHolder] = Modal.useModal();
  const [selectedItem, setSelectedItem] = useState<EntFinancialIndicators>(new EntFinancialIndicators());
  const [showModal, setShowModal] = useState<boolean>(false);
  const [form] = useForm();
  const { t } = useTranslation()
  const authState = useSelector((state: RootState) => state.authSlice);

  const [getReportFinancialIndicators] = useReportFinancialIndicatorsMutation();
  const [getArchiveFolders] = useLazyGetFinancialIndicatorsListFilteredQuery();
  const {
	filter,
	fetching,
	data,
	access,
	totalItems,
	setData,
	setFetching,
	setFilter,
	scrollHandler,
	setCurrentPage
  } = useGetDataOnScroll<EntFinancialIndicators, EntFinancialIndicatorsFilter>({
	initialFilter: new EntFinancialIndicatorsFilter(),
	query: getArchiveFolders
  });

  const {
	onAddFinancialIndicators,
	onEditFinancialIndicators,
	onDeleteFinancialIndicators
  } = useFinancialIndicators(modal, () => {
	searchHandleClick();
  })
  const [height, setHeight] = useState<number>(0);

  const storedData = JSON.parse(localStorage.getItem("availableRoutes") || "[]");
  const isReportAvailable = storedData.some((route: any) => (route.alias === "/report" || route.alias === "/interactive-map-ws") && route.methodPost);

  const openAddModal = (item: EntFinancialIndicators) => {
	setSelectedItem(item);
	setShowModal(true);
  }

  const setEntityFromFields = (): EntFinancialIndicatorsFilter => {
	let filt: EntFinancialIndicatorsFilter = _.clone(filter);
	filt.governmentIds = form.getFieldValue(FormField.governmentIds) ?? null;
	filt.periodStart = form.getFieldValue(FormField.periodStart) ?? null;
	filt.periodEnd = form.getFieldValue(FormField.periodEnd) ?? null;
	filt.page = 1;
	return filt;
  }

  const clearFields = () => {
	form.setFields([
	  { name: FormField.governmentIds, value: null },
	  { name: FormField.periodStart, value: null },
	  { name: FormField.periodEnd, value: null },
	]);
  }

  const searchHandleClick = () => {
	setFilter(setEntityFromFields());
	setData([]);
	setCurrentPage(0);
	setFetching(true);
  }

  const handleSubmit = async () => {
	try {
	  const payload = {
		governmentAlias: authState.governmentAlias,
		language: i18n.language,
	  };

	  const result = await getReportFinancialIndicators(payload);

	  if ('data' in result) {
		const blob = result.data;
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", `Report_${ dayjs().format("YYYY-MM-DD") }.xlsx`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		message.success(t('reports.download_success'));
	  } else {
		throw new Error(t('reports.download_failed'));
	  }
	} catch (error: any) {
	  Modal.error({
		title: t("msg.error"),
		content: error.message || t('reports.download_failed'),
	  });
	}
  };

  const table = (
	<div style={ { maxHeight: `${ height }px`, overflow: 'auto' } } onScroll={ scrollHandler }>
	  <table>
		<thead>
		<tr>
		  <th style={ { width: "40px" } }>#</th>
		  <th style={ { width: "280px" } }>{ t('user.organization') } </th>
		  <th>{ t('financial_indicators.beginning') } </th>
		  <th>{ t('financial_indicators.ending') }</th>
		  <th>{ t('financial_indicators.total_budget') }</th>
		  <th> { t('financial_indicators.total_expenditure') }</th>
		  <th>{ t('financial_indicators.remaining_budget') }</th>
		  <th style={ { width: "120px" } }></th>
		</tr>
		</thead>
		<tbody>
		{ fetching ? (
		  <tr>
			<th colSpan={ 7 }>
			  <Spin spinning={ fetching } style={ {
				width: '100%',
				height: '180px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center'
			  } }/>
			</th>
		  </tr>
		) : null }
		{ data.length !== 0 ? data.map((item, index) => (
		  <tr key={ item.id }>
			<td>{ ++index }</td>
			<td>{ i18n.language === 'ru' ? item.government?.nameRu : item.government?.nameKy }</td>
			<td>{ item.periodStart } </td>
			<td>{ item.periodEnd }</td>
			<td>{ item.budgetTotal }</td>
			<td>{ item.expensesTotal }</td>
			<td>{ item.remainingBudget }</td>
			<td>
			  <Flex gap="small">
				{ access && access.methodPut && (
				  <Tooltip title={ t('general.edit') }>
					<Button className='btn_edit' onClick={ () => {
					  openAddModal(item)
					} } type="primary" shape="circle" icon={ <EditOutlined/> }/>
				  </Tooltip>
				) }
				{ access && access.methodDelete && (
				  <Tooltip title={ t('general.delete') }>
					<Button className='btn_edit' danger onClick={ () => {
					  onDeleteFinancialIndicators(item)
					} } type="primary" shape="circle" icon={ <DeleteOutlined/> }/>
				  </Tooltip>
				) }
			  </Flex>
			</td>
		  </tr>
		)) : (
		  <tr>
			<td colSpan={ 9 }>
			  <EmptyData/>
			</td>
		  </tr>
		) }
		</tbody>
	  </table>
	</div>
  );

  return (
	<>
	  <PageHeader
		title={ t('titles.financial_indicators') }
		additionalInfo={
		  <Typography.Text style={ { marginBottom: '0.5em', display: 'block' } } type="secondary">
			{ t('general.records_found') } { totalItems }
		  </Typography.Text>
		}
	  />
	  { access && access.methodPost && (
		<Button type="primary" icon={ <PlusOutlined/> } onClick={ () => {
		  openAddModal(new EntFinancialIndicators())
		} }>
		  { t('general.add') }
		</Button>
	  ) }
	  { isReportAvailable && (
		<Button style={ { marginLeft: '5px' } } type="primary" icon={ <PlusOutlined/> }
				onClick={ () => handleSubmit() }>
		  { t('reports.download_report') }
		</Button>
	  ) }
	  <ContentPartFilter
		filter={ <FilterFinancialIndicators
		  onSearch={ searchHandleClick }
		  onReset={ () => {
			clearFields();
			searchHandleClick()
		  } }
		  formField={ FormField }
		  form={ form }
		/> }
		setHeight={ setHeight }
		table={ table }
	  />

	  <AddFinancialIndicatorsModal
		financialIndicators={ selectedItem }
		show={ showModal }
		onHide={ () => {
		  setShowModal(false)
		} }
		onSubmit={ selectedItem.id !== null ? onEditFinancialIndicators : onAddFinancialIndicators }
	  />
	  { contextHolder }
	</>
  );
}
